<template>
  <ion-page id="explorer-page">

    <AlertReplacement :alertInfo="alertInfo" :trigger="alertTrigger"
              @alert-result="alertResult"></AlertReplacement>

    <ion-content :scroll-y="false" class="ion-no-padding">

      <!-- Left Side -->
      <div id="the_board" :style="styleBoard">
        <CMChessboard :fen="fen" :validMoves="availableMoves"
                    :orientation="orientation" 
                    :boardSize="boardSize" @board-move="boardMove" />
      </div>

      <div id="the_movehistory" 
                :style="styleMoveHistory" 
                :class="{ 'text-dark' : isDark, 'text-light' : !isDark }">
          <span v-if="moveHistoryStack.length == 0">
            <b>{{ t("Rating") }}: {{ barMinValue }} {{ t("to") }} {{ barMaxValue }}</b>
          </span>
          <MoveHistory :moveHistoryStack="moveHistoryStack" :trigger="moveHistoryTrigger" 
                          @move-history-string="moveHistoryStringUpdate"/>
      </div>

        <!-- Right Side -->
        <div v-show="showState == SHOW_AI_ANALYSIS " :style="styleAIAnalysis"
           class="max-size info-left-padding" :class="{ 
                                'text-dark' : isDark, 
                                'text-light' : !isDark, 
                                'info-window-dark' : isDark && !streamIsWorking,
                                'info-window-light' : !isDark && !streamIsWorking,
                                'info-still-working-dark' : isDark && streamIsWorking,
                                'info-still-working-light' : !isDark && streamIsWorking,
                                }">
          <div v-html="markdownHTML" :class="{ 'info-still-working' : streamIsWorking }"></div>
        </div>

        <div v-show="showState == SHOW_MOVES_GRAPH">
          <div id="the_slice_movelist"
                      :class="{ 'text-dark' : isDark,'text-light' : !isDark }" :style="styleMoveList">
            <MoveListSelector :resultsData="availableMoves" :eighty="eighty"
                  :moveNumber="moveNumberValue"
                  :showCheckBoxes=true
                  :showAnalysisVerdict = true
                  :moveListTrigger="availableMovesTrigger" 
                  @checkbox-click="moveListCheckboxClick" 
                  @move-click="moveListMoveClick" />
          </div>

          <div id="the_slice_chart"
                  :class="{ 'text-dark' : isDark,'text-light' : !isDark }" 
                  :style="styleChart">
            <div>
              <SliceDiceAgChart :moveList="availableMoves" :moveNumber="moveNumberValue" 
                  :showAdvantage="showWhiteAdvantage" :moveListTrigger="availableMovesTrigger" 
                  :styleHeight="styleChartAgHeight"/> 
            </div>
          </div>
        </div>

        <ActionSheet heading="Options" :items="actionSheetButtons" 
              :trigger="actionSheetTrigger"
              @action-sheet-result="actionSheetResult">
        </ActionSheet>
    </ion-content>
    <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
  </ion-page>
</template>

<script setup>

  import { IonItem, IonList, IonButton, IonRange,IonPage, IonContent, modalController } from '@ionic/vue';

  import { t } from "@/composables/translateText.js";

  import ToolBar from '@/components/ToolBar.vue';
  import Toolbar from '@/components/Toolbar.vue';
  import AlertReplacement from '@/components/AlertReplacement.vue'

  import MoveListSelector from '@/components/MoveListSelector.vue'
  import SliceDiceAgChart from '@/components/SliceDiceAgChart.vue'

  import CMChessboard from '@/components/CMChessboard.vue'
  import MoveHistory from '@/components/MoveHistory.vue'

  import ActionSheet from '@/components/ActionSheet.vue'
  import ModalExplorerRange from '@/components/ModalExplorerRange.vue'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'
  import ModalGenericSingleList from '@/components/ModalGenericSingleList.vue'

  import { ref, watch } from 'vue'
  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'
  import {  getOS, useGetData , getLichessData, getChesscomData, getWebSiteURL,
              generateErrorAlertText } from '@/composables/getData.js'

  import { useConnectionStore } from '@/stores/connectionStore';
  import { onMounted, onBeforeUnmount } from 'vue'

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { availableWidthHeight, calculateChessBoardRect, isScreenPortraitOrientation,
            buildStyleToolbar, buildStyleTopline,buildStyleMiddle } 
            from '@/composables/screenSize'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import MarkdownIt from "markdown-it";
  import removeMd from 'remove-markdown';
  import { renderChessboardSvg } from '@/composables/fenToSVG'

  const md = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true
  });

  const SHOW_MOVES_GRAPH = 0
  const SHOW_AI_ANALYSIS = 1
  
  const showState = ref(SHOW_MOVES_GRAPH)
  const TOOLBAR_BACK = 0
  const TOOLBAR_FLIP = 1
  const TOOLBAR_RATING = 2
  const TOOLBAR_AI_GENERATE = 3
  const TOOLBAR_OPTIONS = 4
 
  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const MOVEHISTORY_FONTSIZE = 16
  const MOVEHISTORY_HEIGHT =  MOVEHISTORY_FONTSIZE * 1.2 * 2 + 4

  const emit = defineEmits(['boardMove'])

  const connectionStore = useConnectionStore()

  var streamIsWorking = ref(false)
  
  const isDark = ref(connectionStore.getDarkmode != "light")
  //const isDark = window.matchMedia('(prefers-color-scheme: dark)');

  const menuTrigger = ref(0)

  var markdownText = ""

  const markdownHTML = ref(md.render(markdownText))

  const alertInfo = ref({})
  const alertTrigger = ref(0)

  const toolbarExplorerElements = [
    { index : TOOLBAR_BACK, label : t("Back") , disabled : true, collapse:false},
    { index : TOOLBAR_FLIP, label : t("Flip") , disabled : false, collapse:false},
    { index : TOOLBAR_RATING, label : t("Rating") , disabled : false },
    { index : TOOLBAR_AI_GENERATE, label : "AI" , disabled : false, collapse:false},
    { index : TOOLBAR_OPTIONS, label : t("Options") , disabled : false, hasAction : true },
    
  ]

  const toolbarElements = ref(toolbarExplorerElements)

  const ACTION_OPTION_PERSONA = 10
  const ACTION_OPTION_FEN = 11
  const ACTION_OPTION_PGN = 12

  var optionActionSheetButtons = [
    { text: "Select Persona",data: { index: ACTION_OPTION_PERSONA } },
    { text: "FEN to Clipboard",data: { index: ACTION_OPTION_FEN } },
    { text: "PGN to Clipboard", data: { index: ACTION_OPTION_PGN } },
    { text: 'Close', role: 'cancel',data: { index : -1}}
  ]

  const actionSheetButtons = ref([])
  const actionSheetTrigger = ref(0)

  const barMin = ref(800);
  const barMax = ref(2550);
  const barStep = ref(50)

  const barMinValue = ref(1000)
  const barMaxValue = ref(2000)

  const personaDisplayArray = [
    { index : 0, name : "Helpful Coach", id : "normal", radio : "normal"},
    { index : 1, name : "Park Hustler", id : "hustler", radio : "hustler"},
    { index : 1, name : "Kid Coach", id : "kids", radio : "kids"},
    { index : 1, name : "Stern Soviet School", id : "stern", radio : "stern"},
    { index : 1, name : "Swashbuckling", id : "swashbuckling", radio : "swashbuckling"},
    { index : 1, name : "Morphy", id : "morphy", radio : "morphy"},
    { index : 1, name : "Shakespeare's Coach", id : "shakespeare", radio : "shakespeare"},
  ]
  var personaDisplaySelected = connectionStore.getAiOpeningPersona
  var optionDisplayArray = []
  var optionDisplaySelected = ""
  var optionHeading = ""

  const whiteAdvantageLabel = ref(t("All") + " ")
  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  const boardSize = ref(100)

  const showDone = ref(true)
  const topLineTitle = ref("AI Opening Explorer")

  const fen = ref(INITIAL_FEN)
  const initialFenid4 = "147869767"
  const initialFenid8 = "1"
  const eighty = ref(-2)
  const showScores = ref(true)

  var playingWhite = 1
  var blankAppendTrigger = " "
  var orientation = ref("white")

  var maxSpreadsheetColumnNumber = 0
  var spreadsheetColumnDefinitions = ref([])

  // what comes back from the server
  var mainMoves = []

  // merged result from the server grabs
  const availableMoves = ref([])

  const moveHistoryStack = ref([])
  const moveHistoryTrigger = ref(0)
  var moveHistoryString = ""
  const toolbarTrigger = ref(0)
  const gameCollectionTrigger = ref(0)

  const moveNumberValue = ref(15)
  const availableMovesTrigger = ref(0)
  const showWhiteAdvantage = ref(false)
  const styleChartAgHeight = ref("height: 500px")

  // these are so the checkboxes and flags are set when going back up the tree
  var saveSelectedSans = []
  var selectedSansStack = []

  // this will get the recommend/avoid/neutral moves that are sent for analysis 
  var analyzedMoves = new Map()
  var analyzedMovesStack = []

  var streamIsWorking = ref(false)
  var streamLineBuffer = ""
  var streamingGame = true
  var userHadEnoughCredits = true

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  const styleChart = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveList = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleAIAnalysis = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveHistory = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : MOVEHISTORY_FONTSIZE + "px",
    "text-align" : "left",
    "line-height" : "1.2em"
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  function afterStartupCallbackRoutine(stuffChanged,errorProgram,errorEntry) {
    if (errorProgram != null)
      openErrorModal(errorProgram,errorEntry)
    if (connectionStore.getIsSubscriber != 1) {
      return
    }
    updateWidthHeights()
    getInitialUserInfo()
  }

  onMounted(() => {
  })

  // START Generic Options List

  const openGenericSingleListModal = async () => {
    const modal = await modalController.create({
      component: ModalGenericSingleList,
      componentProps: {
        displayArray : optionDisplayArray,
        initialValue : optionDisplaySelected,
        title : optionHeading      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      // did user leave the select the same?
      if (data.selected == optionDisplaySelected)
        return

      console.log("CONFIRM in single",data)
      // only drop down is persona so do it here

      connectionStore.setAiOpeningPersona(data.selected)
      personaDisplaySelected = data.selected
   }
  };

  const openRatingModal = async () => {
    const modal = await modalController.create({
      component: ModalExplorerRange,
      componentProps: {
        min : barMinValue.value,
        max : barMaxValue.value
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      var didChange = false
      if (data.lower != barMinValue.value) {
        didChange = true
        barMinValue.value = data.lower
      }
      if (data.upper != barMaxValue.value) {
        didChange = true
        barMaxValue.value = data.upper
      }

      //console.log("CONFIRM",data,didChange)

      if (didChange)
        updateUserInfo("rating")
    }
  };

  function actionSheetResult(action,index) {
    if (action != "yes")
      return
    switch (index) {
      case ACTION_OPTION_PERSONA :
        console.log("show persona list")
        optionHeading = t("Persona")
        optionDisplayArray = personaDisplayArray
        optionDisplaySelected = personaDisplaySelected
        openGenericSingleListModal()
        break
      case ACTION_OPTION_FEN :
        const copyFen = fen.value.trim()
        var fenPieces = copyFen.split(" ")
        fenPieces[5] = 1
        const fenFixed = fenPieces.join(" ")
        //console.log("fenfixed",fenFixed)
        navigator.clipboard.writeText(fenFixed);
        break
      case ACTION_OPTION_PGN :
        buildPGN()
        break
    }
  }

  function resetExplorerScreen() {
    // reset to beginning
    afterStartupCallbackRoutine(true)
  }

  watch(() => windowSizeStore.getWindowSize, () => {
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    updateWidthHeights()
    menuTrigger.value++
  })

  function updateStyleObjects(boundaries) {

    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())

    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleMoveList.value.height = boundaries['moveList'].height + "px"
    styleMoveList.value.width = boundaries['moveList'].width + "px"
    styleMoveList.value.top = boundaries['moveList'].top + "px"
    styleMoveList.value.left = boundaries['moveList'].left + "px"

    styleMoveHistory.value.height = boundaries['history'].height + "px"
    styleMoveHistory.value.width = boundaries['history'].width + "px"
    styleMoveHistory.value.top = boundaries['history'].top + "px"
    styleMoveHistory.value.left = boundaries['history'].left + "px"

    styleChart.value.height = boundaries['chart'].height + "px"
    styleChart.value.width = boundaries['chart'].width + "px"
    styleChart.value.top = boundaries['chart'].top + "px"
    styleChart.value.left = boundaries['chart'].left + "px"

    styleChartAgHeight.value = "height: " + (boundaries['chart'].height) + "px"

    styleAIAnalysis.value.height = boundaries['ai'].height + "px"
    styleAIAnalysis.value.width = boundaries['ai'].width + "px"
    styleAIAnalysis.value.top = boundaries['ai'].top + "px"
    styleAIAnalysis.value.left = boundaries['ai'].left + "px"
  }

  function updateWidthHeights() {
    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    updateStyleObjects(boundaries)
  }

  function calculatePortraitWidthHeights(availableSpace) {

    // Vertically -> Board, move history, (move list & chart side by side), sliders
    const availableSpaceForBoard = { width : availableSpace.width ,
                height : availableSpace.height / 2.5,
                isPortrait : true }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)
    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const historyPositionTop = boardPositionTop + boardHeight
    const historyPositionLeft = availableSpace.leftRightIndent
    const historyHeight = MOVEHISTORY_HEIGHT
    const historyWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const moveListPositionTop = historyPositionTop  + historyHeight
    const moveListPositionLeft = availableSpace.leftRightIndent
    const moveListHeight = availableSpace.height - moveListPositionTop - MOVEHISTORY_FONTSIZE * 1.2
    const moveListWidth = 270

    const chartPositionTop = moveListPositionTop
    const chartPositionLeft = moveListPositionLeft + moveListWidth
    const chartHeight = moveListHeight
    const chartWidth = availableSpace.width - moveListWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      moveList : {
        width : moveListWidth,
        height : moveListHeight,
        left : moveListPositionLeft + availableSpace.leftIndent,
        top : moveListPositionTop+ availableSpace.topIndent
      },
      history : {
        width : historyWidth,
        height : historyHeight,
        left : historyPositionLeft + availableSpace.leftIndent,
        top : historyPositionTop+ availableSpace.topIndent
      },
      chart : {
        width : chartWidth,
        height : chartHeight,
        left : chartPositionLeft + availableSpace.leftIndent,
        top : chartPositionTop+ availableSpace.topIndent
      },
      ai : {
        width : moveListWidth + chartWidth,
        height : moveListHeight,
        left : moveListPositionLeft + availableSpace.leftIndent,
        top : moveListPositionTop+ availableSpace.topIndent
      },
    }
    return (result)
  }

  function calculateLandscapeWidthHeights(availableSpace) {

    // reserve space for the instruction lines
    const availableSpaceForBoard = { width : availableSpace.width/4,
                height : availableSpace.height * .75,
                isPortrait : false }
    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const moveListPositionTop = 0
    const moveListPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const moveListHeight = availableSpace.height
    const moveListWidth = 270

    const chartPositionTop = 0
    const chartPositionLeft = moveListPositionLeft + moveListWidth
    const chartHeight = availableSpace.height
    const chartWidth = availableSpace.width - chartPositionLeft

    const historyPositionTop = boardPositionTop + boardHeight + 5
    const historyPositionLeft = availableSpace.leftRightIndent
    const historyHeight = availableSpace.height - historyPositionTop
    const historyWidth = boardWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      moveList : {
        width : moveListWidth,
        height : moveListHeight,
        left : moveListPositionLeft + availableSpace.leftIndent,
        top : moveListPositionTop+ availableSpace.topIndent
      },
      history : {
        width : historyWidth,
        height : historyHeight,
        left : historyPositionLeft + availableSpace.leftIndent,
        top : historyPositionTop+ availableSpace.topIndent
      },
      chart : {
        width : chartWidth,
        height : chartHeight,
        left : chartPositionLeft + availableSpace.leftIndent,
        top : chartPositionTop+ availableSpace.topIndent
      },
      ai : {
        width : moveListWidth + chartWidth,
        height : moveListHeight,
        left : moveListPositionLeft + availableSpace.leftIndent,
        top : moveListPositionTop+ availableSpace.topIndent
      },
    }
    return (result)
  }

  function numberMovesSelected() {
    // count # of selected items to set the AI button
    var count = 0;
    for (var i=0; i < availableMoves.value.length; i++)
      if (availableMoves.value[i].selected)
        count++
    return (count)
  }

  // For Slice & Dice
  function moveListCheckboxClick(item) {
    //console.log("move list checkbox",item)
    availableMoves.value[item.index].selected = item.flag
    availableMovesTrigger.value++
    const count = numberMovesSelected()
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AI_GENERATE,(count == 0))
  }

  function moveListMoveClick(index) {
    boardMove(index)
  }

  // End for Slice & Dice

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function updateValues(e) {
    barMinValue.value = e.minValue;
    barMaxValue.value = e.maxValue;   
    if (barMaxValue.value == barMinValue.value) {
      if (barMinValue.value < barMax.value)
        barMaxValue.value = barMinValue.value + 50
      else
        barMinValue.value = barMaxValue.value - 50
    }
  }

  function setSliderValues() {
    if (barMaxValue.value > barMax.value)
      barMaxValue.value = barMax.value
    if (barMinValue.value < barMin.value)
      barMinValue.value = barMin.value
  }

  function formatDate(date,separator) {
    const year = date.toLocaleString('default', {year: 'numeric'});
    const month = date.toLocaleString('default', {
      month: '2-digit',
    });
    const day = date.toLocaleString('default', {day: '2-digit'});

    return [year, month, day].join(separator);
  }

  function moveHistoryStringUpdate(moveString) {
    //console.log("Move history String ",moveString)
    moveHistoryString = moveString
  }

  function buildPGN() {
      
    const todayDate = formatDate(new Date(),".")
    var bigString = "[Event \"Chessvis.com\"]\n"
    bigString += "[Site \"Explorer PGN\"]\n"
    bigString += "[Date \"" + todayDate + "\"]\n"
    bigString += "[Round \"1\"]\n"
    bigString += "[White \"White\"]\n"
    bigString += "[Black \"Black\"]\n"
    bigString += "[Result \"*\"]\n\n"
    bigString += moveHistoryString
    bigString += " *\n\n"

    //console.log("PGN",bigString)
    navigator.clipboard.writeText(bigString);
  }


  function alertResult(response,task,info) {
    //console.log("Alert Result",response,task,fen.value)

    if (response == "no")
      return

    if (response == "fen") {
      const copyFen = fen.value.trim()
      var fenPieces = copyFen.split(" ")
      fenPieces[5] = 1
      const fenFixed = fenPieces.join(" ")
      //console.log("fenfixed",fenFixed)
      navigator.clipboard.writeText(fenFixed);
    }
    else {
      buildPGN()
    }
  }

  function toplineClick(whatClicked) {

  //console.log("SOURCE",source)
  
  emit("menu-item-click","ai_main_menu")
}

  function toolbarClick(index) {
    const element = findToolbarElement(toolbarElements.value,index)
    if (element.disabled)
      return

    switch (index) {
      case TOOLBAR_AI_GENERATE :
        if (showState.value == SHOW_AI_ANALYSIS) {
          setToolbarElementLabel(toolbarElements.value,TOOLBAR_AI_GENERATE,"AI")
          showState.value = SHOW_MOVES_GRAPH
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,(moveHistoryStack.value.length >= 1) ? false : true)
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_FLIP,false)
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_RATING,false)
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_OPTIONS,false)
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AI_GENERATE,false)
        }
        else {
          setToolbarElementLabel(toolbarElements.value,TOOLBAR_AI_GENERATE,t("Moves"))
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,true)
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_FLIP,true)
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_RATING,true)
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_OPTIONS,true)
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AI_GENERATE,true)
          showState.value = SHOW_AI_ANALYSIS
          moveAnalysis()
        }
        break
      case TOOLBAR_OPTIONS :
        actionSheetButtons.value = optionActionSheetButtons
        actionSheetTrigger.value++
        break
      // MOVE THIS TO AN OPTIONS ACTION SHEET
      // case TOOLBAR_COPY :
      //   alertInfo.value = { header : t("Copy to Clipboard?") , 
      //           message : t("'FEN' of current board OR entire 'PGN'?"), type : "fenpgn", task : "fenpgn"  }
      //   alertTrigger.value++
      //   break
      case TOOLBAR_RATING :
        openRatingModal()
        break
      case TOOLBAR_FLIP :
        incrementAppendTrigger()
        playingWhite = 1 - playingWhite
        orientation.value = (playingWhite == 1) ? "white" + blankAppendTrigger : "black" + blankAppendTrigger
        updateUserInfo("color")
        break
      case TOOLBAR_BACK :
        if (moveHistoryStack.value.length <= 1) {
          fen.value = INITIAL_FEN + " "

          console.log("In Toolbar back stack",analyzedMovesStack)
          saveSelectedSans = (selectedSansStack.length) ? selectedSansStack[0] : []
          analyzedMoves = (analyzedMovesStack.length) ? analyzedMovesStack[0] : new Map()
          console.log("In Toolbar back analyzed moves",analyzedMoves)
          selectedSansStack = []
          analyzedMovesStack = []

          selectedSansStack.pop()
          analyzedMovesStack.pop()

          moveHistoryStack.value = []
          moveHistoryTrigger.value++
          getAvailableMoves(initialFenid4,initialFenid8,100,false)
        }
        else {
          saveSelectedSans = selectedSansStack[selectedSansStack.length - 1]
          selectedSansStack.pop()
          analyzedMoves = analyzedMovesStack[analyzedMovesStack.length - 1]
          analyzedMovesStack.pop()

          moveHistoryStack.value.pop()
          moveHistoryTrigger.value++

          fen.value = moveHistoryStack.value[moveHistoryStack.value.length - 1].fen

          getAvailableMoves(moveHistoryStack.value[moveHistoryStack.value.length - 1].fenid4,
                moveHistoryStack.value[moveHistoryStack.value.length - 1].fenid8,
                moveHistoryStack.value[moveHistoryStack.value.length - 1].movePercentage,false)
        }
        break    
    }
  }

  // Start Streaming

  function findRecommendAvoidMoves() {
    const recMatch = markdownText.match(/^Recommend:\s*(.+)$/m);
    const avoidMatch = markdownText.match(/^Avoid:\s*(.+)$/m);

    const recommend = recMatch ? recMatch[1] : 'none';
    const avoid = avoidMatch ? avoidMatch[1] : 'none';

    // Overwrite with R or A from the response
    if (recommend !== 'none') {
        recommend.split(',').map(m => m.trim()).forEach(san => analyzedMoves.set(san,'R'));
    }
    if (avoid !== 'none') {
        avoid.split(',').map(m => m.trim()).forEach(san => analyzedMoves.set(san,'A'));
    }
    console.log("Analyzed MOves",analyzedMoves)

    for (var i=0; i < availableMoves.value.length; i++) {
      const verdict = analyzedMoves.get(availableMoves.value[i].san)
      availableMoves.value[i].analysisVerdict = (verdict == null) ? "" : verdict
    }
  }

  function moveAnalysis() {
    markdownHTML.value = md.render("<h1>*** Running Opening Explorer Analysis</h1>")
    markdownText = ""
    streamWithIterator("ai_opening_explorer_response.php")
  }

  function safeJSONParse(str, fallback = null) {
      try {
          return JSON.parse(str);
      } catch {
          console.log("Parse Failed ->",str)
          return fallback;
      }
  }

  function handlePacket(json) {
    if (!json.hasOwnProperty('text')) {
      //console.log("NOT TEXT",json)
      if (json.hasOwnProperty("lacking")) {
        //console.log("********************* NOT ENOUGH CREDITS ****************")
        userHadEnoughCredits = false
        markdownHTML.value = md.render("<h1>*** Not Enough Credits ***</h1>")
      }
      return
    }
    markdownText += json.text
    markdownHTML.value = md.render(markdownText)
  }

  // Called when the stream closes
  function onStreamEnd() {
      // Handle anything left in the buffer
      const trimmed = streamLineBuffer.trim();
      if (trimmed) {
          try {
              const packet = JSON.parse(trimmed);
              handlePacket(packet);
          } catch (e) {
              console.error('Incomplete packet at stream end:', trimmed, e);
          }
      }
      streamLineBuffer = '';
  }

  // Called with each raw chunk from your PHP stream
  function onChunk(chunk) {
      //console.log("ON CHUNK ",chunk)
      streamLineBuffer += chunk;
      
      // Split on newlines — we may have 0, 1, or several complete lines
      const lines = streamLineBuffer.split('\n');
      
      // Everything except the last element is a complete line
      // The last element is the incomplete remainder — put it back in the buffer
      streamLineBuffer = lines.pop();
      
      for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;  // skip blank lines
          
          try {
              const packet = JSON.parse(trimmed);
              handlePacket(packet);
          } catch (e) {
              console.error('Bad JSON line:', trimmed, e);
          }
      }
  }

  var controller = null; // We keep this in the outer scope

  async function streamWithIterator(programName) {

    //console.log("in stream")
    // 2. Initialize a new AbortController for this specific request
    controller = new AbortController();
    const signal = controller.signal;

    userHadEnoughCredits = true

    const websiteURL = getWebSiteURL()

    const url = websiteURL + programName

    const params = new URLSearchParams();

    params.append("persona",personaDisplaySelected)
    params.append("rating_min",barMinValue.value)
    params.append("rating_max",barMaxValue.value)
    params.append("move_count",numberMovesSelected())

    params.append("color",((moveHistoryStack.value.length % 2) == 0) ? "White" : "Black")
    var percentages = []
    var sans = []
    var gameCounts = []

    analyzedMoves.clear()
    for (var i=0; i < availableMoves.value.length; i++)
      if (availableMoves.value[i].selected) {
        percentages.push(availableMoves.value[i].percentagesString)
        sans.push(availableMoves.value[i].san)
        gameCounts.push(availableMoves.value[i].countDisplay)
        analyzedMoves.set(availableMoves.value[i].san,"N")
      }
    params.append("percentages",percentages.join("\t"))
    params.append("sans",sans.join("\t"))
    params.append("game_counts",gameCounts.join("\t"))

    var movesPlayed = []
    for (var i=0; i < moveHistoryStack.value.length; i++)
      movesPlayed.push(moveHistoryStack.value[i].san)
    params.append("moves_played",movesPlayed.join(" "))

    // cookie too
    const cookie = connectionStore.getCookie
    const dummy = connectionStore.getDummy

    params.append("cookie",cookie)
    params.append("dummyUser",dummy)
    params.append("calltype","2")
    params.append("cook",getOS(true))  

    console.log("PARMS",params)

    streamIsWorking.value = true

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            },
            body: params,
            signal: signal // Attach the signal here
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        var lineBuffer = ""

        while (true) {
            const { value, done } = await reader.read();
            if (done) {
              console.log("stream done",value)
              findRecommendAvoidMoves()
              streamIsWorking.value = false
              setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AI_GENERATE,false)
              break;
            }

            const text = decoder.decode(value);
            onChunk(text)
        }      
    } catch (err) {
        if (err.name === 'AbortError') {
              console.log( "\n[Generation stopped by user]")
        } else {
            console.error("Stream error:", err);
        }
    } finally {
        // 3. Reset UI state regardless of success or failure
        controller = null;
    }
    onStreamEnd()
    console.log("stream end of function")
    findRecommendAvoidMoves()
    streamIsWorking.value = false
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AI_GENERATE,false)
  }


  // End Streaming


  function boardMove(arrayIndex) {
    //console.log("board move ",arguments)

    // save current move's sans before getting next ply
    saveSelectedSans = []
    for (var i=0; i < availableMoves.value.length; i++)
      if (availableMoves.value[i].selected)
        saveSelectedSans.push(availableMoves.value[i].san)
    selectedSansStack.push(saveSelectedSans)
    saveSelectedSans = []

    console.log("in boardmove analyszed moves",analyzedMoves)

    // need to push independent copy
    analyzedMovesStack.push(new Map(analyzedMoves))
    analyzedMoves.clear()

    fen.value = availableMoves.value[arrayIndex].fen
    
    const fenid4 = availableMoves.value[arrayIndex].fenid4
    const fenid8 = availableMoves.value[arrayIndex].fenid8
    const san = availableMoves.value[arrayIndex].san
    const boardFromSquare = availableMoves.value[arrayIndex].from
    const boardToSquare = availableMoves.value[arrayIndex].to
    const previousMovePercentage = availableMoves.value[arrayIndex].movePercentage

    const oneMove = {fen : fen.value, fenid4 : fenid4, fenid8 : fenid8,
                          boardToSquare : boardToSquare, 
                          boardFromSquare : boardFromSquare,
                          san : san, movePercentage : previousMovePercentage,
                          openingName : "" }
    moveHistoryStack.value.push(oneMove)
    moveHistoryTrigger.value++

    getAvailableMoves(fenid4,fenid8,previousMovePercentage,false)
  }

  function createPercentageString(moves) {
    const indexToMove15 = 2
    const arrayAtMove15 = moves[indexToMove15]
    return (arrayAtMove15.join(" "))
  }

  function processAvailableMovesJsonResponse (json) {
    //console.log("*** in process json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    console.log("Analyzed MOves in process Json",analyzedMoves)

    const whiteString = json['white']
    const blackString = json['black']
    const whites = whiteString.split("\t")
    const blacks = blackString.split("\t")
    const sans = json['san'].split("\t")
    const move_percentages = json['percentage'].split("\t")
    const pretty_game_counts = json['pretty'].split("\t")

    //if was a recalc then update the movehistory stack
    const recalc_percentages_string = String(json['recalc_percentages'])
    if (recalc_percentages_string.length > 0) {
      const recalc_percentages = recalc_percentages_string.split("\t")
      for (var i = 0; i < recalc_percentages.length; i++)
        moveHistoryStack.value[i].movePercentage = recalc_percentages[i]
    }

    const fens = json['fen'].split("\t")
    const fenid4s = json['fenid4'].split("\t")
    const fenid8s = []
    const froms = json['from'].split("\t")
    const tos = json['to'].split("\t")

    var inRange = json['in_range'].split("\t")

    const count = json['count']

    var scores = []
    const weights = [ -6, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 6]

    eighty.value = 100
    
    scores = json['scores'].split("\t")

    availableMoves.value = []

    for (var i=0; i < count; i++) {
      const whiteNumber = Number(whites[i])
      const blackNumber = Number(blacks[i])
      const totalNumber = whiteNumber + blackNumber

      const whitePercent = (totalNumber > 0) ? whiteNumber * 100 / totalNumber : 0
      const blackPercent = (totalNumber > 0) ? blackNumber * 100 / totalNumber : 0
      const whitePercentString = whitePercent.toFixed(1)
      const blackPercentString = blackPercent.toFixed(1)

      // build the bar chart info
      var moves = []
      var weightedScores = []
      var isSelected = false

      const moveScores = scores[i].split(",")
      for (var moveIndex = 0; moveIndex < 5; moveIndex++) {
        const offset = moveIndex * 15
        var weight = 0
        var percentages = []
        for (var percentIndex = 0; percentIndex < 15; percentIndex++) {
          const oneNumber = Number(moveScores[offset + percentIndex])
          weight += oneNumber * weights[percentIndex]
          percentages.push(oneNumber)
        }
        moves.push(percentages)
        weightedScores.push((weight/100).toFixed(2))
      }

      // if there is a saved selected list, use it, otherwise use the first two
      if (saveSelectedSans != null && saveSelectedSans.length) {
        var isSelected = false
        for (var j=0; j < saveSelectedSans.length; j++)
          if (sans[i] == saveSelectedSans[j]) {
            isSelected = true
            break
          }
      }
      else
        isSelected = (i < 2)

      const analysisVerdict = analyzedMoves.get(sans[i])
      
      const percentage = Number(move_percentages[i])
      const displayPercentage = (inRange[i] != "1") ? "all" : percentage.toFixed(2) + "%"

      const oneMove = { index: i, 
                        fenid4: fenid4s[i],
                        fenid8 : 0,
                        white : whitePercentString, black : blackPercentString, 

                        // Slice & Dice
                        weightedScores : weightedScores,
                        selected : isSelected,
                        moves : moves,

                        from : froms[i], to: tos[i],
                        countDisplay : pretty_game_counts[i],
                        textType : "player",
                        san : sans[i], 
                        percentDisplay : displayPercentage,
                        movePercentage : move_percentages[i],
                        fen : fens[i],
                        openingName : "" ,
                        percentagesString : createPercentageString(moves),
                        analysisVerdict : (analysisVerdict == null) ? "" : analysisVerdict
                      }
      availableMoves.value.push(oneMove)
    }

    availableMovesTrigger.value++

    // update the toolbar
    const ply = moveHistoryStack.value.length
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,(ply >= 1) ? false : true)
    toolbarTrigger.value++
  }

  function debounce(callback, interval=500) {
    let debounceTimeoutId;

    return function(...args) {
      clearTimeout(debounceTimeoutId);
      debounceTimeoutId = setTimeout(() => callback.apply(this, args), interval);
    };
  }

  function getAvailableMoves(fenid4,fenid8,previousMovePercentage,doRecalc) {
      getAvailableDatabaseMoves(fenid4,fenid8,previousMovePercentage,doRecalc)
  }

  function getAvailableDatabaseMoves(fenid4,fenid8,previousMovePercentage,doRecalc) {
    // get the previous move stack if recalc
    var recalcString = ""

    if (doRecalc && moveHistoryStack.value.length > 0) {
      var recalcFenids = []
      for (var i = 0; i < moveHistoryStack.value.length; i++)
        recalcFenids.push(moveHistoryStack.value[i].fenid4)
      recalcString = recalcFenids.join(",")
    }

    var formData = {
      parent_unique : "0",
      fenid4 : fenid4,
      previous_percent : previousMovePercentage,
      white_to_move : 1 - (moveHistoryStack.value.length % 2),
      playing_white : playingWhite,
      min_rating : barMinValue.value,
      max_rating : barMaxValue.value - 1,
      recalc_fenid4s : recalcString
    }

    // zap waiting for next move values
    availableMoves.value = []
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,true)

    eighty.value = -2

    //console.log("before post for availablemoves -> ",formData)

    useGetData("chessvis_opening_move.php", formData, processAvailableMovesJsonResponse)
  }

  function getUserMoves(fenid4,fenid8) {
    var formData = {
      fenid4 : fenid4, 
      fenid8 : fenid8,
      player_white : playingWhite,
      white_to_move : 1 - (moveHistoryStack.value.length % 2),
      is_main : "main",
      future : "yes",
    }

    // zap waiting for next move values
    availableMoves.value = []

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,false)
    toolbarTrigger.value++

    eighty.value = -2
    //console.log("before post to get USER moves-> ",formData)

    useGetData("chessvis_opening_slice_dice.php", formData, processAvailableMovesJsonResponse)
  }

  function processInitialJsonResponse (json) {
    //console.log(" in initial json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    barMinValue.value = Number(json['rating_min'])

    // on the server, the value is 49 & 99 but for display, let's increment it by 1
    barMaxValue.value = Number(json['rating_max']) + 1

    playingWhite = parseInt(json['playing_white'])
    if (playingWhite == 0)
      orientation.value = "black"

    setSliderValues(true)
    getAvailableMoves(initialFenid4,initialFenid8,100,false)
    menuTrigger.value++
  }

  function getInitialUserInfo() {
    var formData = { }
    useGetData("chessvis_opening_info.php", formData, processInitialJsonResponse)
  }

  function processUpdateUserJsonResponse(json) {
    //console.log(" in update user json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    if (moveHistoryStack.value.length == 0)
      getAvailableMoves(initialFenid4,initialFenid8,100,true)
    else
      getAvailableMoves(moveHistoryStack.value[moveHistoryStack.value.length - 1].fenid4,
                moveHistoryStack.value[moveHistoryStack.value.length - 1].fenid8,
                moveHistoryStack.value[moveHistoryStack.value.length - 1].movePercentage,true)
  }

  function updateUserInfo(updateAction) {
    var formData = { 
      action : updateAction,
      rating_max : barMaxValue.value -1,
      rating_min : barMinValue.value,
      playing_white : playingWhite
    }

    console.log("formdata for update user info",formData)
    useGetData("chessvis_opening_update_user_info.php", formData, processUpdateUserJsonResponse)
  }
  
</script>

<style scoped>

/* ion-content {
    padding-top: var(--ion-safe-area-top, 0);
    padding-bottom: var(--ion-safe-area-bottom, 0);
    padding-left: var(--ion-safe-area-left, 0);
    padding-right: var(--ion-safe-area-right, 0);
  } */

  ion-list {
    padding-top : 0px;
    padding-bottom : 0px;
  }
.move_slider_style {
  width : 50px;
}

.info-left-padding {
  padding-left: 5px;
}

.info-window-dark {
  background-color : rgb(30,30,30);
}
.info-window-light {
  background-color : #e5e5e5;
}

.info-still-working-dark {
  background-color: #49494c;
}

.info-still-working-light {
  background-color: #b7b7b7;
}

</style>
